<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleCreate">
                <i class="ri-add-line"></i>
                新增推播活動
              </button>
      </div>
    </div>

    <div class="page-container">
    <!-- 依結果分頁：草稿、進行中、已完成/已取消 -->
    <div class="broadcast-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="tab-btn"
        :class="{ active: activeTab === tab.key }"
        @click="handleTabChange(tab.key)"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- 已完成 Tab 說明 -->
    <div v-if="activeTab === 'completed'" class="tab-tip">
      <i class="ri-information-line"></i>
      僅顯示近一年內（{{ oneYearAgoLabel }} 之後）的已完成與已取消推播記錄。
    </div>

    <div class="page-toolbar">
      <select
        v-model="filterSendKind"
        class="form-control filter-select"
        @change="handleFilterChange"
      >
        <option value="">全部類型</option>
        <option value="immediate">即時</option>
        <option value="scheduled">預約</option>
        <option value="recurring">週期</option>
      </select>
      <select
        v-model="selectedLineConfigId"
        class="form-control filter-select"
        @change="onLineConfigChange"
      >
        <option value="">全部官方帳號</option>
        <option
          v-for="config in lineConfigs"
          :key="config.id"
          :value="config.id"
        >
          {{ config.configName || config.name || config.id }}
        </option>
      </select>
      <div class="search-box">
        <input
          v-model="searchKeyword"
          type="text"
          class="form-control"
          placeholder="搜尋活動名稱..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group" v-if="activeTab === 'active'">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="scheduled">已排程</option>
          <option value="preparing">準備受眾</option>
          <option value="sending">發送中</option>
          <option value="failed">失敗</option>
        </select>
      </div>
      <div class="filter-group" v-else-if="activeTab === 'completed'">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="completed">已完成</option>
          <option value="cancelled">已取消</option>
        </select>
      </div>
      <button class="btn btn-secondary" @click="loadBroadcasts()">
        <i class="ri-refresh-line"></i> 重新整理
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="loadBroadcasts()">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>官方帳號</th>
            <th>活動名稱</th>
            <th>訊息類型</th>
            <th>受眾類型</th>
            <th>目標人數</th>
            <th>已發送</th>
            <th v-if="showStatusColumn">狀態</th>
            <th>排程時間</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="displayRows.length === 0">
            <td :colspan="showStatusColumn ? 10 : 9" class="empty-state">
              沒有找到推播活動
            </td>
          </tr>
          <tr v-for="row in displayRows" :key="row.key">
            <td>{{ formatLineConfigNames(row.lineConfigIds) }}</td>
            <td class="campaign-cell">
              <span class="send-kind" :class="'send-kind-' + row.sendKind">{{ formatSendKind(row.sendKind) }}</span>
              <strong>{{ row.title }}</strong>
            </td>
            <td>
              <span class="badge badge-info">{{ formatMessageType(row.messageType) }}</span>
            </td>
            <td>
              <span class="badge badge-secondary">{{ formatAudienceType(row.audienceType) }}</span>
            </td>
            <td>{{ row.rowKind === 'recurring' ? '—' : (row.targetCount ?? '-') }}</td>
            <td>{{ row.rowKind === 'recurring' ? `${row.actualSentCount || 0} 次` : (row.actualSentCount || 0) }}</td>
            <td v-if="showStatusColumn">
              <span :class="getRowStatusClass(row)">
                {{ formatRowStatus(row) }}
              </span>
            </td>
            <td>{{ formatDate(row.scheduledAt) }}</td>
            <td>{{ formatDate(row.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <template v-if="row.rowKind === 'recurring'">
                  <button
                    class="btn btn-sm btn-secondary"
                    @click="handleRecurringEdit(row.id)"
                  >
                    <i class="ri-edit-line"></i> 編輯
                  </button>
                  <button
                    v-if="row.status === 'active'"
                    class="btn btn-sm btn-warning"
                    @click="handleRecurringPause(row.id, row.title)"
                  >
                    <i class="ri-pause-line"></i> 暫停
                  </button>
                  <button
                    v-if="row.status === 'paused'"
                    class="btn btn-sm btn-success"
                    @click="handleRecurringResume(row.id, row.title)"
                  >
                    <i class="ri-play-line"></i> 恢復
                  </button>
                  <button
                    class="btn btn-sm btn-danger"
                    @click="handleRecurringArchive(row.id, row.title)"
                  >
                    <i class="ri-delete-bin-line"></i> 刪除
                  </button>
                </template>
                <template v-else>
                  <button
                    v-if="row.status === 'draft'"
                    class="btn btn-sm btn-primary"
                    @click="handleEdit(row.id)"
                  >
                    <i class="ri-edit-line"></i> 編輯
                  </button>
                  <button
                    v-if="row.status === 'draft'"
                    class="btn btn-sm btn-danger"
                    @click="handleDelete(row.id, row.title)"
                  >
                    <i class="ri-delete-bin-line"></i> 刪除
                  </button>
                  <button
                    class="btn btn-sm btn-secondary"
                    @click="handleView(row.id)"
                  >
                    <i class="ri-eye-line"></i> 詳情
                  </button>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="pagination.total > 0"
      :page="pagination.page"
      :total-pages="pagination.totalPages"
      :total="pagination.total"
      @change="handlePageChange"
    />
    </div>
  </div>
</template>

<script>
import { usePageTitle } from '@/composables/usePageTitle.js';
import lineBroadcastService, { lineBroadcastRecurringService } from '@/api/modules/lineBroadcast.js';
import lineConfigApi from '@/api/modules/lineConfig.js';
import { showSuccess, showError, showConfirm } from '@/utils/swal.js';

export default {
  name: 'LineBroadcastList',
  setup() {
    const { pageTitle } = usePageTitle('LINE 推播管理');
    return { pageTitle };
  },
  computed: {
    oneYearAgo() {
      const d = new Date();
      d.setFullYear(d.getFullYear() - 1);
      return d.toISOString();
    },
    oneYearAgoLabel() {
      const d = new Date();
      d.setFullYear(d.getFullYear() - 1);
      return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
    },
    showStatusColumn() {
      return this.activeTab === 'active' || this.activeTab === 'completed';
    },
    showRecurringRows() {
      return this.activeTab === 'active'
        && !this.filterStatus
        && (!this.filterSendKind || this.filterSendKind === 'recurring');
    },
    displayRows() {
      const broadcasts = this.broadcasts.map(broadcast => ({
        key: `b-${broadcast.id}`,
        rowKind: 'broadcast',
        id: broadcast.id,
        title: broadcast.campaignName,
        sendKind: broadcast.sendKind || this.inferSendKind(broadcast),
        lineConfigIds: broadcast.lineConfigIds,
        messageType: broadcast.messageType,
        audienceType: broadcast.audienceType,
        targetCount: broadcast.targetCount,
        actualSentCount: broadcast.actualSentCount || 0,
        status: broadcast.status,
        scheduledAt: broadcast.scheduledAt,
        createdAt: broadcast.createdAt
      }));

      if (!this.showRecurringRows || this.pagination.page !== 1) {
        return broadcasts;
      }

      const recurring = this.recurringConfigs.map(cfg => ({
        key: `r-${cfg.id}`,
        rowKind: 'recurring',
        id: cfg.id,
        title: cfg.configName,
        sendKind: 'recurring',
        lineConfigIds: cfg.lineConfigIds,
        messageType: cfg.messageType,
        audienceType: cfg.audienceType,
        targetCount: null,
        actualSentCount: cfg.executionCount || 0,
        status: cfg.status,
        scheduledAt: cfg.nextScheduledAt,
        createdAt: cfg.createdAt
      }));

      return [...recurring, ...broadcasts];
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      activeTab: 'active',
      tabs: [
        { key: 'draft', label: '草稿' },
        { key: 'active', label: '進行中' },
        { key: 'completed', label: '已完成/已取消' }
      ],
      searchKeyword: '',
      filterStatus: '',
      filterSendKind: '',
      selectedLineConfigId: '',
      lineConfigs: [],
      broadcasts: [],
      recurringConfigs: [],
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0
      },
      searchTimeout: null
    };
  },
  mounted() {
    this.loadLineConfigs();
  },
  methods: {
    async loadLineConfigs() {
      try {
        const response = await lineConfigApi.list({ pageSize: 100 });
        if (response?.success && response.data) {
          const items = response.data.items || response.data || [];
          this.lineConfigs = Array.isArray(items) ? items : [];
        }
      } catch (err) {
        console.error('載入 LINE 官方帳號列表失敗:', err);
      }
      this.loadBroadcasts();
    },

    onLineConfigChange() {
      this.pagination.page = 1;
      this.loadBroadcasts();
    },

    formatLineConfigNames(lineConfigIds) {
      const ids = Array.isArray(lineConfigIds) ? lineConfigIds : [];
      if (ids.length === 0) return '-';
      const names = ids
        .map(id => this.lineConfigs.find(c => c.id === id)?.configName || this.lineConfigs.find(c => c.id === id)?.name)
        .filter(Boolean);
      return names.length > 0 ? names.join('、') : ids.length === 1 ? '—' : `${ids.length} 個帳號`;
    },

    async loadBroadcasts() {
      this.loading = true;
      this.error = null;

      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        };

        if (this.searchKeyword) {
          params.search = this.searchKeyword;
        }

        if (this.selectedLineConfigId) {
          params.lineConfigId = this.selectedLineConfigId;
        }

        if (this.filterSendKind) {
          params.sendKind = this.filterSendKind;
        }

        if (this.activeTab === 'draft') {
          params.status = 'draft';
        } else if (this.activeTab === 'completed') {
          params.status = this.filterStatus || 'completed,cancelled';
          params.fromDate = this.oneYearAgo;
        } else if (this.filterStatus) {
          params.status = this.filterStatus;
        } else {
          params.excludeStatus = 'draft,completed,cancelled';
        }

        const includeRecurring = this.showRecurringRows;
        const [response, recurringItems] = await Promise.all([
          lineBroadcastService.getBroadcasts(params),
          includeRecurring ? this.fetchRecurringConfigs() : Promise.resolve([])
        ]);
        this.recurringConfigs = recurringItems;

        if (response && response.success && response.data) {
          this.broadcasts = response.data.items || response.data || [];
          if (response.data.pagination) {
            this.pagination = {
              ...this.pagination,
              total: response.data.pagination.total || response.data.pagination.totalItems || 0,
              totalPages: response.data.pagination.totalPages || Math.ceil((response.data.pagination.total || response.data.pagination.totalItems || 0) / this.pagination.pageSize)
            };
          } else if (response.pagination) {
            this.pagination = {
              ...this.pagination,
              total: response.pagination.total || response.pagination.totalItems || 0,
              totalPages: response.pagination.totalPages || Math.ceil((response.pagination.total || response.pagination.totalItems || 0) / this.pagination.pageSize)
            };
          }
        } else {
          this.error = (response && response.message) || '獲取推播列表失敗';
        }
      } catch (err) {
        console.error('載入推播列表失敗:', err);
        this.error = err.response?.data?.message || err.message || '獲取推播列表失敗';
      } finally {
        this.loading = false;
      }
    },

    handleTabChange(tab) {
      this.activeTab = tab;
      this.filterStatus = '';
      this.searchKeyword = '';
      this.pagination.page = 1;
      this.loadBroadcasts();
    },

    async fetchRecurringConfigs() {
      const params = { page: 1, pageSize: 100 };
      if (this.searchKeyword) params.search = this.searchKeyword;
      if (this.selectedLineConfigId) params.lineConfigId = this.selectedLineConfigId;

      const [activeRes, pausedRes] = await Promise.all([
        lineBroadcastRecurringService.list({ ...params, status: 'active' }),
        lineBroadcastRecurringService.list({ ...params, status: 'paused' })
      ]);

      const items = [];
      if (activeRes?.success && activeRes.data) {
        items.push(...(activeRes.data.items || []));
      } else if (activeRes && activeRes.success === false) {
        throw new Error(activeRes.message || '載入週期推播設定失敗');
      }
      if (pausedRes?.success && pausedRes.data) {
        items.push(...(pausedRes.data.items || []));
      } else if (pausedRes && pausedRes.success === false) {
        throw new Error(pausedRes.message || '載入週期推播設定失敗');
      }
      return items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },

    handleRecurringEdit(id) {
      this.$router.push({ name: 'ClientLineBroadcastRecurringEdit', params: { id } });
    },

    async handleRecurringPause(id, configName) {
      const confirmed = await showConfirm('確認暫停', `確定要暫停「${configName}」的週期推播嗎？`);
      if (!confirmed) return;
      try {
        const res = await lineBroadcastRecurringService.pause(id);
        if (res?.success) {
          await showSuccess('已暫停', '週期推播已暫停');
          this.loadBroadcasts();
        } else {
          await showError('暫停失敗', res?.message || '操作失敗');
        }
      } catch (err) {
        await showError('暫停失敗', err.response?.data?.message || err.message || '操作失敗');
      }
    },

    async handleRecurringResume(id, configName) {
      const confirmed = await showConfirm('確認恢復', `確定要恢復「${configName}」的週期推播嗎？`);
      if (!confirmed) return;
      try {
        const res = await lineBroadcastRecurringService.resume(id);
        if (res?.success) {
          await showSuccess('已恢復', '週期推播已恢復');
          this.loadBroadcasts();
        } else {
          await showError('恢復失敗', res?.message || '操作失敗');
        }
      } catch (err) {
        await showError('恢復失敗', err.response?.data?.message || err.message || '操作失敗');
      }
    },

    async handleRecurringArchive(id, configName) {
      const confirmed = await showConfirm('確認刪除', `確定要刪除「${configName}」的週期推播設定嗎？此操作無法復原。`);
      if (!confirmed) return;
      try {
        const res = await lineBroadcastRecurringService.archive(id);
        if (res?.success) {
          await showSuccess('已刪除', '週期推播設定已刪除');
          this.loadBroadcasts();
        } else {
          await showError('刪除失敗', res?.message || '操作失敗');
        }
      } catch (err) {
        await showError('刪除失敗', err.response?.data?.message || err.message || '操作失敗');
      }
    },

    formatRecurringStatus(status) {
      const map = { active: '執行中', paused: '已暫停', archived: '已封存' };
      return map[status] || status;
    },

    getRecurringStatusClass(status) {
      const map = { active: 'badge badge-success', paused: 'badge badge-warning', archived: 'badge badge-secondary' };
      return map[status] || 'badge badge-secondary';
    },

    handleSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.loadBroadcasts();
      }, 500);
    },

    inferSendKind(broadcast) {
      if (broadcast?.recurringConfigId) return 'recurring';
      const scheduledAt = broadcast?.scheduledAt ? new Date(broadcast.scheduledAt).getTime() : null;
      if (broadcast?.status === 'draft') {
        return scheduledAt ? 'scheduled' : 'immediate';
      }
      const createdAt = broadcast?.createdAt ? new Date(broadcast.createdAt).getTime() : null;
      if (scheduledAt != null && createdAt != null && scheduledAt - createdAt > 90 * 1000) {
        return 'scheduled';
      }
      return 'immediate';
    },

    formatSendKind(kind) {
      const map = { immediate: '即時', scheduled: '預約', recurring: '週期' };
      return map[kind] || '即時';
    },

    formatRowStatus(row) {
      if (row.rowKind === 'recurring') return this.formatRecurringStatus(row.status);
      return this.formatStatus(row.status);
    },

    getRowStatusClass(row) {
      if (row.rowKind === 'recurring') return this.getRecurringStatusClass(row.status);
      return this.getStatusBadgeClass(row.status);
    },

    handleFilterChange() {
      this.pagination.page = 1;
      this.loadBroadcasts();
    },

    handlePageChange(page) {
      this.pagination.page = page;
      this.loadBroadcasts();
    },

    handleCreate() {
      this.$router.push({ name: 'ClientLineBroadcastCreate' });
    },

    handleView(id) {
      this.$router.push({ name: 'ClientLineBroadcastDetail', params: { id } });
    },

    handleEdit(id) {
      this.$router.push({ name: 'ClientLineBroadcastEdit', params: { id } });
    },

    async handleDelete(id, campaignName) {
      const confirmed = await showConfirm(
        '確認刪除推播',
        `確定要刪除推播活動「${campaignName}」嗎？此操作無法復原。`
      );
      if (!confirmed) return;

      try {
        const response = await lineBroadcastService.deleteBroadcast(id);
        if (response && response.success) {
          await showSuccess('刪除成功', '推播活動已刪除');
          this.loadBroadcasts();
        } else {
          await showError('刪除失敗', response?.message || '刪除推播活動失敗');
        }
      } catch (err) {
        await showError('刪除失敗', err.response?.data?.message || err.message || '刪除推播活動失敗');
      }
    },

    formatMessageType(type) {
      const typeMap = {
        'text': '文字', 'flex': 'Flex', 'image': '圖片',
        'video': '影片', 'audio': '音訊', 'location': '位置',
        'sticker': '貼圖', 'imagemap': '圖片地圖', 'template': '範本', 'carousel': '輪播'
      };
      return typeMap[type] || type || '-';
    },

    formatAudienceType(type) {
      const typeMap = {
        'all': '全部好友', 'tags': '標籤篩選', 'members': '手動選擇',
        'line_tags': 'LINE 標籤', 'rule': '受眾規則',
        'segment': '受眾群組', 'custom_rule': '自訂規則'
      };
      return typeMap[type] || type || '-';
    },

    formatStatus(status) {
      const statusMap = {
        'draft': '草稿', 'scheduled': '已排程', 'preparing': '準備受眾', 'sending': '發送中',
        'prepared': '準備中', 'completed': '已完成', 'failed': '失敗', 'cancelled': '已取消'
      };
      return statusMap[status] || status || '-';
    },

    getStatusBadgeClass(status) {
      const classMap = {
        'draft': 'badge badge-secondary', 'scheduled': 'badge badge-info',
        'preparing': 'badge badge-warning',
        'sending': 'badge badge-warning', 'prepared': 'badge badge-warning',
        'completed': 'badge badge-success',
        'failed': 'badge badge-danger', 'cancelled': 'badge badge-secondary'
      };
      return classMap[status] || 'badge badge-secondary';
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('zh-TW', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.broadcast-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 20px;
}

.tab-btn {
  padding: 10px 24px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  font-size: 14px;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;

  &:hover {
    color: #333;
  }

  &.active {
    color: #2563eb;
    border-bottom-color: #2563eb;
  }
}

.tab-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  margin-bottom: 16px;
  background: #f0f4ff;
  border: 1px solid #c5d3f5;
  border-radius: 6px;
  font-size: 13px;
  color: #4a5568;

  i {
    color: #5a74c5;
    font-size: 15px;
    flex-shrink: 0;
  }
}

.table-wrapper table {
  min-width: 1200px;
}

.filter-select {
  width: 200px;
  flex-shrink: 0;
}

.campaign-cell {
  white-space: nowrap;
}

.send-kind {
  display: inline-block;
  margin-right: 8px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.6;
  vertical-align: middle;
}

.send-kind-immediate {
  background: #e8f5e9;
  color: #2e7d32;
}

.send-kind-scheduled {
  background: #e3f2fd;
  color: #1565c0;
}

.send-kind-recurring {
  background: #f3e8ff;
  color: #6b21a8;
}
</style>
